// Marke „Demo-Daten“ (docs/research/03-recht-und-compliance.md §15): Ein Demo-Betrieb sagt an jeder Stelle der
// Hülle, dass Personen, Kunden, Preise und Bestände Beispielwerte sind – ruhig, ohne Warnfarbe. Der Satz kommt aus
// der Sitzung (tenant.demoNotice) und erscheint beim Überfahren, per Tastatur und auf dem Telefon durch Tippen.
import { useSession } from '../api/session'
import { cn } from '../lib/cn'
import { Popover } from '../ui/Popover'

export const DEMO_LABEL = 'Demo-Daten'
/** Satz für Demo-Betriebe ohne eigenen Hinweis */
export const DEMO_NOTICE_FALLBACK = 'Demo-Daten – Personen, Kunden, Preise und Bestände sind Beispielwerte.'
/** Satz unter den Demo-Zugängen der Anmeldung (dort gibt es noch keine Sitzung und keinen Betrieb) */
export const DEMO_LOGIN_NOTICE = 'Personen, Kunden, Preise und Bestände dieser Betriebe sind Beispielwerte.'

/** Hinweissatz des angemeldeten Betriebs – null, wenn er kein Demo-Betrieb ist. */
export function useDemoNotice(): string | null {
  const tenant = useSession()?.tenant
  if (!tenant?.demo) return null
  return tenant.demoNotice ?? DEMO_NOTICE_FALLBACK
}

export interface DemoMarkProps {
  /** Telefon-Kopfzeile: kurzes Wort „Demo“, Ziel 44 px hoch. Sonst (Seitenleiste): „Demo-Daten“ unter dem Betriebsnamen */
  compact?: boolean
  className?: string
}

export function DemoMark({ compact, className }: DemoMarkProps) {
  const notice = useDemoNotice()
  if (!notice) return null
  return (
    <Popover
      openOnHover
      side="bottom"
      align={compact ? 'end' : 'start'}
      width="sm"
      ariaLabel="Hinweis zu den Demo-Daten"
      trigger={
        <button
          type="button"
          data-testid="demo-mark"
          // der sichtbare Text steht am Anfang des Namens; der Satz selbst folgt für Screenreader sofort
          aria-label={notice.startsWith(DEMO_LABEL) ? notice : `${DEMO_LABEL}: ${notice}`}
          className={cn(
            'group/demo flex shrink-0 items-center rounded-xs',
            // Telefon-Kopfzeile: volles Touch-Ziel. Seitenleiste: so hoch wie die Marke, auf Touch vergrößert `hit-area`
            compact ? 'min-h-11 min-w-11 justify-center' : 'hit-area h-5',
            className,
          )}
        >
          <span className="inline-flex h-4.5 items-center rounded-xs border border-line-strong px-1 text-micro font-medium tracking-normal whitespace-nowrap text-fg-muted normal-case transition-colors duration-(--duration-instant) group-hover/demo:border-fg-subtle group-hover/demo:text-fg group-aria-expanded/demo:text-fg">
            {compact ? 'Demo' : DEMO_LABEL}
          </span>
        </button>
      }
    >
      <p className="text-dense text-fg">{notice}</p>
    </Popover>
  )
}
